import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";

const SalesTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("");
  const [coachName, setCoachName] = useState("");
  const [orderDetails, setOrderDetails] = useState({});
  const [productMap, setProductMap] = useState({});
  const { t , i18n} = useTranslation();

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";

  useEffect(() => {
     const token = localStorage.getItem("token");
        
            if (!token) {
              setRole("unauthenticated");
              setLoading(false);
              return;
            }
        
            try {
              const decodedToken = jwtDecode(token);
              const { userId } = decodedToken;
          
              if (!userId) {
                console.error("No userId found in token");
                setRole("unauthenticated");
                return;
              }
          
              setLoading(true);
  
              fetch(`${API_URL}account/${userId}`)
              .then(res => res.json())
              .then(data => {
                setRole(data.role);
                setCoachName(`${data.firstName} ${data.lastName}`);
              })
              .catch(err => {
                console.error("Failed to fetch user info", err);
                setRole("unauthenticated");
                setCoachName("Unknown Coach");
              })
              .finally(() => setLoading(false));
          } catch (err) {
            console.error("Failed to decode token", err);
            setRole("unauthenticated");
            setCoachName("Unknown Coach");
            setLoading(false);
          }
        }, [API_URL]);

  const fetchTransactions = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}transactions`)
      .then(res => res.json())
      .then(data => {
        setTransactions(data);
        setFiltered(data);
      })
      .catch(err => console.error("Failed to fetch transactions", err))
      .finally(() => setLoading(false));
  }, [API_URL]);

  const isValidMongoId = (id) => /^[a-f\d]{24}$/i.test(id);
  const fetchUserById = useCallback(async (userId) => {
    if (!userId || userMap[userId] || !isValidMongoId(userId)) return;
  
    try {
      const res = await fetch(`${API_URL}account/${userId}`);
      const data = await res.json();
      setUserMap(prev => ({ ...prev, [userId]: data }));
    } catch (err) {
      console.error(`Failed to fetch user ${userId}`, err);
    }
  }, [API_URL, userMap]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    filtered.forEach(tx => {
      const userId = tx.metadata?.userId;
      if (userId) {
        fetchUserById(userId);
      }
    });
  }, [filtered, fetchUserById]);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
  
    const filteredResults = transactions.filter(tx => {
      const coachLine = tx.description?.toLowerCase() || ''; // this is where the coach is mentioned
      const metaClientName = tx.metadata?.clientName?.toLowerCase() || '';
      const transactionId = tx.id?.toLowerCase() || '';
  
      const userId = tx.metadata?.userId;
      const userFromMap = userMap[userId];
      const mongoClientName = userFromMap
        ? `${userFromMap.firstName} ${userFromMap.lastName}`.toLowerCase()
        : '';
  
      const matchesSearch =
        coachLine.includes(term) ||
        metaClientName.includes(term) ||
        mongoClientName.includes(term) ||
        transactionId.includes(term);
  
      const isCoachMatch = role === "admin"
        ? true
        : coachLine.includes(`coach: ${coachName.toLowerCase()}`);
  
      return matchesSearch && isCoachMatch;
    });
  
    setFiltered(filteredResults);
  }, [searchTerm, transactions, userMap, role, coachName]);

const fetchOrderById = useCallback(async (orderId) => {
  if (!orderId || orderDetails[orderId]) return;

  try {
    const res = await fetch(`${API_URL}orders/${orderId}`);
    const data = await res.json();
    if (!data.error) {
      setOrderDetails(prev => ({ ...prev, [orderId]: data }));
    }
  } catch (err) {
    console.error(`❌ Failed to fetch order ${orderId}:`, err);
  }
}, [API_URL, orderDetails]);

useEffect(() => {
  filtered.forEach(tx => {
    const orderId = tx.metadata?.orderId;
    if (orderId && !orderDetails[orderId]) {
      fetchOrderById(orderId);
    }
  });
}, [filtered, fetchOrderById, orderDetails]);

const fetchProductById = useCallback(async (productId) => {
  if (!productId || productMap[productId]) return;

  try {
    const res = await fetch(`${API_URL}product/${productId}`);
    const data = await res.json();

    if (!data.error) {
      setProductMap(prev => ({ ...prev, [productId]: data }));
    }
  } catch (err) {
    console.error(`❌ Failed to fetch product ${productId}:`, err);
  }
}, [API_URL, productMap]);

useEffect(() => {
  Object.values(orderDetails).forEach(order => {
    order.products.forEach(p => {
      if (p.productId) {
        fetchProductById(p.productId);
      }
    });
  });
}, [orderDetails, fetchProductById]);

  return (
    <Wrapper>
      <Title>{t("salesTab.title")}</Title>
      <p>
  {role === "admin"
  ? t("salesTab.viewingAllSales")
  : t("salesTab.viewingSalesFor", { coachName })}
</p>
      <SearchContainer>
        <SearchBar type="text" placeholder={t("salesTab.search")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}/>
        <RefreshButton onClick={fetchTransactions}>↻</RefreshButton>
      </SearchContainer>
      {loading ? (
  <p>{t("salesTab.loading")}</p>
) : (
  <CardContainer>
    {filtered.map((tx) => (
      <Card key={tx.id}>
        <Row><strong>{t("salesTab.transactionId")}</strong> {tx.id}</Row>
        <Row><strong>{t("salesTab.coachAndNotes")}</strong> {tx?.description}</Row>
        <Row>
          <strong>🙋‍♀️ Client:</strong>{" "}
          {tx.metadata?.userId ? (
            isValidMongoId(tx.metadata.userId)
              ? (
                  userMap[tx.metadata.userId]
                    ? `${userMap[tx.metadata.userId].firstName} ${userMap[tx.metadata.userId].lastName}`
                    : "Loading..."
                )
              : tx.metadata.userId
          ) : "N/A"}
        </Row>
        <Row><strong>📧 Email:</strong> {tx.metadata?.customer_email || "N/A"}</Row>
        <Row><strong>📅 Date:</strong> {new Date(tx.created * 1000).toLocaleString()}</Row>
        {/* <Row><strong>🧾 Order ID:</strong> {tx.metadata?.orderId || "N/A"}</Row> */}
        <Row><strong>{t("salesTab.amount")}</strong> ${tx.amount_total / 100}</Row>
       {tx.metadata?.cartSummary ? (
  <Row>
    <strong>🥗 {t("salesTab.products")}:</strong> {tx.metadata.cartSummary}
  </Row>
) : (
  <Row>
    <strong>🥗 {t("salesTab.products")}:</strong>{" "}
    {tx.metadata?.orderId && orderDetails[tx.metadata.orderId] ? (
      <ul>
        {orderDetails[tx.metadata.orderId].products.map((p, index) => {
          const product = productMap[p.productId];
          const name = product?.name?.[i18n.language] || `Product ${p.productId}`;
          return (
            <li key={index}>
      {name} × {p.quantity} ({t(`priceType.${p.priceType}`)})
    </li>
          );
        })}
      </ul>
    ) : (
      "Loading..."
    )}
  </Row>
)}
      </Card>
    ))}
  </CardContainer>
)}
    </Wrapper>
  );
};

export default SalesTab;

const Wrapper = styled.div`
  background-position: center;
  box-shadow: 2px 8px 20px black;
  padding: 5rem;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 12px;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: rgba(148,163,135,1);
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 0.7rem;
  font-size: 1rem;
  margin-bottom: 2rem;
  border: 1px solid #ccc;
  border-radius: 12px;
`;

const Card = styled.div`
  background: rgb(209, 208, 207);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 15px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
`;

const Row = styled.div`
  margin-bottom: 0.7rem;
  font-size: 1rem;
`;

const RefreshButton = styled.button`
  background: rgba(186,204,171,1);
  border: none;
  border-radius: 10px;
  padding: 0.6rem 1rem;
  font-size: 1rem;
  color: white;
  margin-bottom: 1rem;
  margin-top: -15px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(148,163,135,1);
  }
`;
const CardContainer = styled.div`
  max-height: 500px; /* You can adjust this */
  overflow-y: auto;
  padding-right: 0.5rem;
  margin-top: 1rem;

  /* Scrollbar glow-up (optional) */
  scrollbar-width: thin;
  scrollbar-color: rgba(148,163,135,1) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(148,163,135,1);
    border-radius: 10px;
  }
`;
const SearchContainer = styled.div`
display: flex;
gap: 1rem;
align-items: center;
margin-bottom: 1.5rem;
`;